import {
  getAmazonCountryCodeFromClientData,
  getCampaignListMinBudget,
  getCampaignListBudgetRule,
  roundCampaignListBudget,
} from "../../campaignList/campaignListV2BudgetConfig";
import {
  BUDGET_CHANGE_TYPE,
  BUDGET_FAMILY,
  DAY_NAMES,
  DAY_NAMES_DISPLAY,
  defaultChangeTypeForBudgetFamily,
  isRestOfAllocatedBudgetType,
} from "./constants";

export function getMinCampaignBudget() {
  return getCampaignListMinBudget();
}

export function getCampaignBudgetCurrencyCode() {
  return getCampaignListBudgetRule().currencyCode;
}

/** Same source as campaign-list-v2: profile currency/country via Intl. */
export function getCampaignBudgetCurrencySymbol() {
  const currencyCode = getCampaignBudgetCurrencyCode();
  const countryCode = getAmazonCountryCodeFromClientData() || "IN";
  const localeCountry = countryCode === "UK" ? "GB" : countryCode;
  try {
    const formatter = new Intl.NumberFormat(`en-${localeCountry}`, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const symbolPart = formatter.formatToParts(1).find((p) => p.type === "currency");
    return symbolPart?.value || currencyCode;
  } catch {
    return currencyCode;
  }
}

export function getCampaignBudgetNumberLocale() {
  const countryCode = getAmazonCountryCodeFromClientData() || "IN";
  return `en-${countryCode === "UK" ? "GB" : countryCode}`;
}

export function roundCampaignBudget(value) {
  return roundCampaignListBudget(value);
}

export function generateId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 11)}`;
}

export function computeProjectedBudget(base, changeType, changeValue) {
  const baseNum = Number(base) || 0;
  const v = changeValue === "" || changeValue == null ? NaN : Number(changeValue);
  if (Number.isNaN(v)) return baseNum;
  if (changeType === BUDGET_CHANGE_TYPE.ABSOLUTE) return v;
  if (changeType === BUDGET_CHANGE_TYPE.PERCENT_DECREASE) return baseNum * (1 - v / 100);
  if (changeType === BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET) {
    return baseNum * (v / 100);
  }
  return baseNum * (1 + v / 100);
}

function toOptionalBudgetNumber(value) {
  if (value === "" || value == null) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export function computeAllocatedAmount(dailyBudget, changeType, changeValue) {
  if (isRestOfAllocatedBudgetType(changeType)) return 0;
  if (changeType === BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET) {
    const valueNum = toOptionalBudgetNumber(changeValue);
    if (valueNum == null) return 0;
    const amount = (Number(dailyBudget) || 0) * (valueNum / 100);
    return Number.isFinite(amount) ? amount : 0;
  }
  if (toOptionalBudgetNumber(changeValue) == null) return 0;
  const projected = computeProjectedBudget(dailyBudget, changeType, changeValue);
  return Number.isFinite(projected) ? projected : 0;
}

export function sumOtherSlotsAllocated(form, currentSlotLocalId, mongoId) {
  const daily =
    form?.campaignTableBudgets?.[mongoId]?.base_value ??
    form?.campaignMeta?.[mongoId]?.defaultBudget ??
    0;
  return (form?.slots ?? []).reduce((sum, slot) => {
    if (slot.localId === currentSlotLocalId) return sum;
    if (isCampaignRestLockedInSlot(form, slot, mongoId)) return sum;
    const row = slot.campaignBudgets?.[mongoId] ?? {};
    const changeType =
      row.change_type ?? defaultChangeTypeForBudgetFamily(form?.budgetFamily);
    return sum + computeAllocatedAmount(daily, changeType, row.change_value);
  }, 0);
}

/**
 * Slot effective budget from daily budget + change type/value.
 * SET types clamp to min/max and return null when value is empty.
 * ALLOCATE types are unclamped so below-min / negative values can surface as errors.
 */
export function computeEffectiveBudget({
  dailyBudget,
  changeType,
  changeValue,
  minBudget,
  maxBudget,
  otherAllocatedTotal = 0,
}) {
  if (isRestOfAllocatedBudgetType(changeType)) {
    const restPercent = toOptionalBudgetNumber(changeValue);
    if (restPercent != null) {
      return roundCampaignBudget((Number(dailyBudget) || 0) * (restPercent / 100));
    }
    const rest = (Number(dailyBudget) || 0) - (Number(otherAllocatedTotal) || 0);
    if (!Number.isFinite(rest)) return null;
    return roundCampaignBudget(rest);
  }

  if (toOptionalBudgetNumber(changeValue) == null) return null;

  const projected = computeProjectedBudget(dailyBudget, changeType, changeValue);
  if (!Number.isFinite(projected)) return null;

  if (changeType === BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET) {
    return roundCampaignBudget(projected);
  }

  let effective = projected;
  const min = toOptionalBudgetNumber(minBudget);
  const max = toOptionalBudgetNumber(maxBudget);
  if (min != null) effective = Math.max(effective, min);
  if (max != null) effective = Math.min(effective, max);

  return roundCampaignBudget(effective);
}

export function getEffectiveBudgetError(effectiveBudget) {
  if (effectiveBudget == null) return "";
  const minBudget = getMinCampaignBudget();
  if (effectiveBudget < minBudget) {
    return `Effective budget must be at least ${minBudget} ${getCampaignBudgetCurrencyCode()}`;
  }
  return "";
}

export function getCumulativeBudgetError(cumulativeBudget) {
  if (cumulativeBudget == null) return "";
  const minBudget = getMinCampaignBudget();
  if (cumulativeBudget < minBudget) {
    return `Effective budget of this and previous slots must total at least ${minBudget} ${getCampaignBudgetCurrencyCode()}`;
  }
  return "";
}

export function getCampaignRestSlotIndex(form, mongoId) {
  return (form?.slots ?? []).findIndex((slot) =>
    isRestOfAllocatedBudgetType(slot?.campaignBudgets?.[mongoId]?.change_type)
  );
}

export function campaignHasRestOfAllocatedBudget(form, mongoId) {
  return getCampaignRestSlotIndex(form, mongoId) >= 0;
}

export function isCampaignRestLockedInSlot(form, slot, mongoId) {
  const restIndex = getCampaignRestSlotIndex(form, mongoId);
  if (restIndex < 0) return false;
  const currentIndex = (form?.slots ?? []).findIndex((s) => s.localId === slot?.localId);
  return currentIndex > restIndex;
}

export function formHasAllSelectedCampaignsRest(form) {
  const ids = form?.selectedCampaignIds ?? [];
  if (!ids.length) return false;
  return ids.every((mid) => campaignHasRestOfAllocatedBudget(form, mid));
}

export function sumPercentOfAllocatedBudget(form, mongoId) {
  const restIndex = getCampaignRestSlotIndex(form, mongoId);
  return (form?.slots ?? []).reduce((sum, slot, index) => {
    if (restIndex >= 0 && index > restIndex) return sum;
    const row = slot.campaignBudgets?.[mongoId] ?? {};
    const changeType =
      row.change_type ?? defaultChangeTypeForBudgetFamily(form?.budgetFamily);
    if (changeType !== BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET) return sum;
    const valueNum = toOptionalBudgetNumber(row.change_value);
    return sum + (valueNum == null ? 0 : valueNum);
  }, 0);
}

export function roundAllocatedPercent(totalPercent) {
  if (!Number.isFinite(totalPercent)) return 0;
  if (Number.isInteger(totalPercent)) return totalPercent;
  return Math.round(totalPercent * 100) / 100;
}

export function getAllocatedPercentTotalError(totalPercent) {
  if (!Number.isFinite(totalPercent) || totalPercent <= 100) return "";
  return `Slot percentages total ${roundAllocatedPercent(totalPercent)}%. Reduce them to 100% or less.`;
}

export function getRestOfAllocatedPercent(form, mongoId) {
  return roundAllocatedPercent(100 - sumPercentOfAllocatedBudget(form, mongoId));
}

function computeAllocateSlotEffective(form, slot, mongoId) {
  if (isCampaignRestLockedInSlot(form, slot, mongoId)) {
    return {
      changeType: BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET,
      effectiveBudget: 0,
      restPercent: null,
    };
  }
  const table = form.campaignTableBudgets?.[mongoId] ?? {};
  const slotRow = slot.campaignBudgets?.[mongoId] ?? {};
  const changeType =
    slotRow.change_type ?? defaultChangeTypeForBudgetFamily(form?.budgetFamily);
  const dailyBudget =
    table.base_value ?? form.campaignMeta?.[mongoId]?.defaultBudget ?? 0;
  const allocateFamily = isAllocateBudgetFamily(form);
  const restPercent = isRestOfAllocatedBudgetType(changeType)
    ? getRestOfAllocatedPercent(form, mongoId)
    : null;
  const effectiveBudget = computeEffectiveBudget({
    dailyBudget,
    changeType,
    changeValue: restPercent ?? slotRow.change_value,
    minBudget: table.min_value,
    maxBudget: table.max_value,
    otherAllocatedTotal: allocateFamily
      ? sumOtherSlotsAllocated(form, slot?.localId, mongoId)
      : 0,
  });
  return { changeType, effectiveBudget, restPercent };
}

/** Sum of effective budgets of all slots before and including the given slot. */
function getCumulativeEffectiveBudget(form, slot, mongoId) {
  const slots = form?.slots ?? [];
  const idx = slots.findIndex((s) => s.localId === slot?.localId);
  if (idx < 0) return null;
  let total = 0;
  for (let i = 0; i <= idx; i += 1) {
    const { effectiveBudget } = computeAllocateSlotEffective(form, slots[i], mongoId);
    if (effectiveBudget == null) return null;
    total += effectiveBudget;
  }
  return roundCampaignBudget(total);
}

export function getAllocateSlotCampaignErrors(form, slot, mongoId) {
  const { changeType, effectiveBudget, restPercent } = computeAllocateSlotEffective(
    form,
    slot,
    mongoId
  );
  if (isCampaignRestLockedInSlot(form, slot, mongoId)) {
    return { percentTotalError: "", effectiveError: "", effectiveBudget: 0, restPercent: null };
  }
  if (!isAllocateBudgetFamily(form)) {
    return { percentTotalError: "", effectiveError: "", effectiveBudget, restPercent: null };
  }
  const percentTotalError =
    changeType === BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET
      ? getAllocatedPercentTotalError(sumPercentOfAllocatedBudget(form, mongoId))
      : "";
  // Min budget applies cumulatively: this slot plus all earlier slots must reach the minimum.
  const effectiveError =
    effectiveBudget != null && effectiveBudget < 0
      ? "Effective budget cannot be negative"
      : getCumulativeBudgetError(getCumulativeEffectiveBudget(form, slot, mongoId));
  return {
    percentTotalError,
    effectiveError,
    effectiveBudget,
    restPercent,
  };
}

export const SCHEDULED_SUMMARY_STATUS = {
  FULLY: "FULLY",
  PARTLY: "PARTLY",
  OVER: "OVER",
};

export function getSlotEffectiveBudgetAmount(form, slot, mongoId) {
  const { effectiveBudget } = computeAllocateSlotEffective(form, slot, mongoId);
  return effectiveBudget == null ? 0 : Number(effectiveBudget) || 0;
}

export function getCampaignScheduledSummary(form, mongoId) {
  const table = form?.campaignTableBudgets?.[mongoId] ?? {};
  const daily = roundCampaignBudget(
    Number(table.base_value ?? form?.campaignMeta?.[mongoId]?.defaultBudget) || 0
  );
  const scheduled = roundCampaignBudget(
    (form?.slots ?? []).reduce(
      (sum, slot) => sum + getSlotEffectiveBudgetAmount(form, slot, mongoId),
      0
    )
  );
  const percent =
    daily > 0 ? roundAllocatedPercent((scheduled / daily) * 100) : scheduled > 0 ? 0 : 100;
  const status =
    scheduled > daily
      ? SCHEDULED_SUMMARY_STATUS.OVER
      : scheduled === daily
        ? SCHEDULED_SUMMARY_STATUS.FULLY
        : SCHEDULED_SUMMARY_STATUS.PARTLY;

  return {
    mongoId,
    name: form?.campaignMeta?.[mongoId]?.name ?? "Campaign",
    daily,
    scheduled,
    percent,
    status,
    unscheduled: roundCampaignBudget(Math.max(0, daily - scheduled)),
    overBy: roundCampaignBudget(Math.max(0, scheduled - daily)),
  };
}

export function getAllocateScheduledSummaries(form) {
  return (form?.selectedCampaignIds ?? []).map((mongoId) =>
    getCampaignScheduledSummary(form, mongoId)
  );
}

export function isAllocateBudgetFamily(form) {
  return (
    form?.automationMode === "DAYPARTING" && form?.budgetFamily === BUDGET_FAMILY.ALLOCATE
  );
}

export function parseNumOrEmpty(val) {
  if (val === "" || val == null) return "";
  const n = Number(val);
  return Number.isNaN(n) ? "" : n;
}

/** Ensures min budget is at least the currency min (default when empty). */
export function enforceMinBudgetFloor(value) {
  const minBudget = getMinCampaignBudget();
  if (value === "" || value == null) return minBudget;
  const num = Number(value);
  if (Number.isNaN(num)) return minBudget;
  return Math.max(minBudget, num);
}

export function isBudgetFloorValid(value) {
  if (value === "" || value == null) return false;
  const num = Number(value);
  return !Number.isNaN(num) && num >= getMinCampaignBudget();
}

export function isMinBudgetValid(value) {
  return isBudgetFloorValid(value);
}

export function isMaxBudgetValid(value) {
  return isBudgetFloorValid(value);
}

export function isDailyBudgetValid(value) {
  return isBudgetFloorValid(value);
}

/** Empty is allowed while editing; invalid numeric / below-floor values return a message. */
function getBudgetFloorError(value, label) {
  if (value === "" || value == null) return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "Enter a valid number";
  const minBudget = getMinCampaignBudget();
  if (num < minBudget) {
    return `${label} must be at least ${minBudget} ${getCampaignBudgetCurrencyCode()}`;
  }
  return "";
}

export function getMinBudgetError(value) {
  return getBudgetFloorError(value, "Min budget");
}

export function getMaxBudgetError(value) {
  return getBudgetFloorError(value, "Max budget");
}

export function getDailyBudgetError(value) {
  return getBudgetFloorError(value, "Daily budget");
}

/** Slot Value is a budget amount only when Budget Type is Number. */
export function getAbsoluteBudgetValueError(value, changeType) {
  if (changeType !== "ABSOLUTE") return "";
  return getBudgetFloorError(value, "Value");
}

export const BUDGET_BULK_MODE = {
  COPY_CAMPAIGN_BUDGET: "COPY_CAMPAIGN_BUDGET",
  INCREASE_PERCENT: "INCREASE_PERCENT",
  DECREASE_PERCENT: "DECREASE_PERCENT",
  SET_ABSOLUTE: "SET_ABSOLUTE",
};

/** @deprecated Use BUDGET_BULK_MODE */
export const MAX_BUDGET_BULK_MODE = BUDGET_BULK_MODE;

export const BUDGET_BULK_MODE_OPTIONS = [
  { value: BUDGET_BULK_MODE.INCREASE_PERCENT, label: "Increase by %" },
  { value: BUDGET_BULK_MODE.DECREASE_PERCENT, label: "Decrease by %" },
  { value: BUDGET_BULK_MODE.SET_ABSOLUTE, label: "Set an absolute value" },
];

export const MAX_BUDGET_BULK_MODE_OPTIONS = [
  { value: BUDGET_BULK_MODE.COPY_CAMPAIGN_BUDGET, label: "Copy campaign budget exactly" },
  ...BUDGET_BULK_MODE_OPTIONS,
];

export function isBudgetBulkPercentMode(mode) {
  return (
    mode === BUDGET_BULK_MODE.INCREASE_PERCENT ||
    mode === BUDGET_BULK_MODE.DECREASE_PERCENT
  );
}

/** Compute a budget value from a base amount for bulk update modes. */
export function computeBudgetFromBase(baseValue, mode, value) {
  const base = Number(baseValue);
  const safeBase = Number.isFinite(base) ? base : 0;

  if (mode === BUDGET_BULK_MODE.COPY_CAMPAIGN_BUDGET) {
    return roundCampaignBudget(safeBase);
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;

  if (mode === BUDGET_BULK_MODE.INCREASE_PERCENT) {
    return roundCampaignBudget(safeBase * (1 + parsed / 100));
  }
  if (mode === BUDGET_BULK_MODE.DECREASE_PERCENT) {
    return roundCampaignBudget(safeBase * (1 - parsed / 100));
  }
  if (mode === BUDGET_BULK_MODE.SET_ABSOLUTE) {
    return roundCampaignBudget(parsed);
  }
  return null;
}

/** Compute max budget from a campaign's daily budget for bulk update modes. */
export function computeMaxBudgetFromCampaignBudget(dailyBudget, mode, value) {
  return computeBudgetFromBase(dailyBudget, mode, value);
}

/** Allow free typing — only block negative numbers. */
export function normalizeMinBudgetInput(value) {
  if (value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  if (num < 0) return "0";
  return value;
}

export function normalizeMaxBudgetInput(value) {
  if (value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  if (num < 0) return "0";
  return value;
}

/** Unescape API validation messages that may contain literal backslash escapes. */
export function formatValidationIssueMessage(message) {
  if (message == null) return "";
  let text = String(message);
  try {
    if (text.startsWith('"') && text.endsWith('"')) {
      text = JSON.parse(text);
    }
  } catch {
    // use raw string
  }
  return text
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t");
}

export function getRuleId(rule) {
  return rule?.id ?? rule?.objectId ?? rule?.object_id;
}

export function getRuleName(rule) {
  return rule?.name ?? "-";
}

export function getRuleUpdatedAt(rule) {
  return rule?.updated_at ?? rule?.updatedAt ?? null;
}

export function getRuleLastRunTime(rule) {
  return rule?.last_run_time ?? rule?.lastRunTime ?? null;
}

export function getScheduleRange(rule) {
  const sc = rule?.schedule_config ?? {};
  return {
    start: sc.start_date ?? "",
    end: sc.end_date ?? "",
  };
}

export function inferAutomationMode(rule) {
  if (rule?.rule_config?.automation_mode) return rule.rule_config.automation_mode;
  const tasks = rule?.rule_tasks ?? [];
  if (tasks.length === 0) return "DAYPARTING";
  const allHaveTimes = tasks.every((t) => {
    const d = t?.rule_action_config?.entity_action_config?.duration_config;
    return d?.start_time && d?.end_time;
  });
  return allHaveTimes ? "DAYPARTING" : "ALL_DAYS_CUSTOM_DAYS";
}

export function getRuleCampaignIds(rule) {
  const ids = new Set();
  (rule?.rule_tasks ?? []).forEach((task) => {
    (task?.entity_configs ?? []).forEach((ec) => {
      if (ec?.entity_type === "campaign" && ec?.id) ids.add(String(ec.id));
    });
  });
  return [...ids];
}

export function getCampaignCount(rule) {
  return getRuleCampaignIds(rule).length;
}

export function getModeLabel(rule) {
  return inferAutomationMode(rule) === "DAYPARTING" ? "Dayparting" : "All / Custom Days";
}

export function formatIsoDateForInput(iso) {
  const s = String(iso ?? "").trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

export function selectedDayIndicesFromFrequency(frequencyValues) {
  if (!Array.isArray(frequencyValues) || frequencyValues.length === 0) {
    return [0, 1, 2, 3, 4, 5, 6];
  }
  return frequencyValues
    .map((d) => DAY_NAMES.indexOf(String(d).toLowerCase()))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);
}

export function frequencyValuesFromDayIndices(indices) {
  if (!indices?.length || indices.length === 7) return null;
  return indices.map((i) => DAY_NAMES[i]);
}

export function mapCampaignStatusForFilter(campaign) {
  const raw = String(campaign?.status ?? "").toLowerCase();
  if (raw === "enabled" || raw === "active" || raw === "live") return "Enabled";
  if (raw === "paused" || raw === "on_hold") return "Paused";
  if (raw === "archived") return "Archived";
  return "Enabled";
}

export function getPortfolioPlatformId(portfolio) {
  const id = portfolio?.platform_id ?? portfolio?.platformId;
  return id == null ? "" : String(id);
}

export function getPortfolioDisplayName(portfolio) {
  return portfolio?.name ?? "-";
}

/** API filters for campaign list search (name), status pills, and optional portfolio. */
export function buildCampaignListApiFilters(
  searchTerm,
  statusFilter,
  portfolioPlatformIds = []
) {
  const filters = [];
  const term = String(searchTerm ?? "").trim();
  if (term) {
    filters.push({ field_name: "name", operator: "CONTAINS", value: term });
  }
  if (statusFilter && statusFilter !== "All") {
    filters.push({
      field_name: "status",
      operator: "EQUALS",
      value: String(statusFilter).toUpperCase(),
    });
  }
  // Backend expects numeric ids (no stringified numbers) for amz_portfolio_id.
  const portfolioIds = [
    ...new Set(
      (portfolioPlatformIds ?? [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n))
    ),
  ];
  if (portfolioIds.length) {
    filters.push({
      field_name: "amz_portfolio_id",
      operator: "IN",
      values: portfolioIds,
    });
  }
  return filters;
}

/** Fetch selected campaigns by mongo id (separate from dropdown search list). */
export function buildSelectedCampaignsApiFilter(campaignIds = []) {
  const ids = [...new Set(campaignIds.map(String).filter(Boolean))];
  if (!ids.length) return [];
  return [{ field_name: "id", operator: "IN", values: ids }];
}

export function isValidCampaignListItem(campaign) {
  return (
    campaign?.status !== "ARCHIVED" &&
    campaign?.platform_id != null &&
    Boolean(getCampaignMongoId(campaign))
  );
}

export function getCampaignMongoId(campaign) {
  return String(campaign?.id ?? campaign?.objectId ?? campaign?._id ?? "");
}

export function getCampaignPlatformId(campaign) {
  return String(campaign?.platform_id ?? campaign?.platformId ?? "");
}

export function getCampaignDisplayName(campaign) {
  return campaign?.name ?? campaign?.campaign_name ?? campaign?.campaignName ?? "-";
}

export function getCampaignBaseBudget(campaign) {
  const v = campaign?.daily_budget ?? campaign?.dailyBudget ?? campaign?.budget ?? 0;
  return Number(v) || 0;
}

/** Green dot for LIVE/active campaigns; grey for expired / other statuses. */
export function isCampaignLiveForIndicator(campaign) {
  const raw = String(campaign?.status ?? "")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return raw === "LIVE" || raw === "ENABLED" || raw === "ACTIVE";
}

export function getCampaignStatusDotColor(campaign) {
  return isCampaignLiveForIndicator(campaign) ? "#22C55E" : "#94A3B8";
}

export function formatDayDisplayName(dayIndex) {
  return DAY_NAMES_DISPLAY[dayIndex] ?? "";
}

export function getCampaignAdSpend(campaign) {
  const v =
    campaign?.ad_spend ??
    campaign?.adSpend ??
    campaign?.spend ??
    campaign?.total_spend ??
    campaign?.daily_budget ??
    campaign?.dailyBudget ??
    campaign?.budget ??
    0;
  return Number(v) || 0;
}

export function formatCampaignBudgetAmount(
  amount,
  currencySymbol = getCampaignBudgetCurrencySymbol()
) {
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  const locale = getCampaignBudgetNumberLocale();
  const formatted = Number.isInteger(safe)
    ? safe.toLocaleString(locale)
    : safe.toLocaleString(locale, { maximumFractionDigits: 2 });
  return `${currencySymbol}${formatted}`;
}

export function formatAdSpendLabel(campaign, currencySymbol = getCampaignBudgetCurrencySymbol()) {
  return `Daily Budget: ${formatCampaignBudgetAmount(getCampaignAdSpend(campaign), currencySymbol)}`;
}
